import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient, botsRoute } from "../../constants.ts";
import {
  BotNotFound,
  Button,
  Collapsible,
  customConfirm,
  inputBaseClass,
  PageSpinner,
} from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

type SubmitParams = {
  setLoading: (v: boolean) => void;
  setErrMsg: (v: string) => void;
  setEmail: (v: string) => void;
  email: string;
  botId: string;
  token: string;
};

const adminErrorMessages: Record<string, string> = {
  "invalid-email": "Please enter a valid email address.",
  "user-not-found": "User not found.",
  "bot-not-found": "Bot not found.",
  "not-permitted":
    "You do not have permission to modify admins for this agent.",
  "billing-user-cannot-leave": "The billing user cannot leave the agent.",
  "not-an-admin": "That user is not an admin of this agent.",
  "cannot-remove-last-admin": "Cannot remove the last admin of the agent.",
};

const formatAdminError = (error: string | undefined) =>
  (error && adminErrorMessages[error]) || error || "Unknown error";

const submit =
  ({ setLoading, setErrMsg, setEmail, email, botId, token }: SubmitParams) =>
  async (e: Event) => {
    e.preventDefault();
    setLoading(true);
    setErrMsg("");
    try {
      await toast.promise(
        (async () => {
          const res = await apiClient({
            endpoint: "add-admin",
            token,
            payload: { botId, email },
          });
          if (!res.success) {
            throw new Error(formatAdminError(res.error));
          }
          return res;
        })(),
        {
          loading: "Adding admin...",
          success: "Admin added!",
          error: ({ message }: Error) => `Could not add admin: ${message}`,
        },
      );
      setEmail("");
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

const leaveBot = (botId: string, token: string) => async () => {
  if (!await customConfirm("Are you sure you want to leave this agent?")) {
    return;
  }
  await toast.promise(
    (async () => {
      const res = await apiClient({
        endpoint: "remove-admin",
        token,
        payload: { botId },
      });
      if (!res.success) {
        throw new Error(formatAdminError(res.error));
      }
      globalThis.location.href = botsRoute;
      return res;
    })(),
    {
      loading: "Leaving agent...",
      success: "You have left the agent",
      error: ({ message }: Error) => message,
    },
  );
};

export const AdminsConfig = ({ id }: { id: string }) => {
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({
    bots: {
      admins: {},
      billingUser: {},
      $: { where: { id } },
    },
  });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  if (isLoading) return <PageSpinner />;
  if (error) return error.message;
  const [bot] = data.bots;
  if (!bot) return <BotNotFound />;
  const admins = bot.admins || [];
  const billingEmail = bot.billingUser?.email;
  const token = user?.refresh_token;
  if (!token) return "Not authenticated";
  return (
    <Collapsible title="Admins">
      <div className="flex flex-col gap-2">
        {admins.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {admins.map((admin: { email?: string }) =>
              admin.email && (
                <span
                  key={admin.email}
                  className="px-2 py-1 text-sm rounded border border-border-default-light dark:border-border-default bg-surface-overlay-light dark:bg-surface-overlay text-text-secondary-light dark:text-text-secondary"
                >
                  {admin.email}
                  {admin.email === billingEmail && (
                    <span className="ml-1 text-xs text-text-tertiary-light dark:text-text-tertiary">
                      (billing)
                    </span>
                  )}
                </span>
              )
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="email"
            required
            value={email}
            onInput={({ currentTarget: { value } }) => setEmail(value)}
            placeholder="Enter admin email"
            className={`flex-1 max-w-md ${inputBaseClass}`}
            disabled={loading}
          />
          <Button
            onClick={submit({
              setLoading,
              setEmail,
              setErrMsg,
              email,
              botId: bot.id,
              token,
            })}
            type="submit"
            disabled={loading}
          >
            Add admin
          </Button>
        </div>
        {errMsg && <div className="text-xs text-danger">{errMsg}</div>}
        {admins.length > 1 && (
          <Button
            onClick={leaveBot(bot.id, token)}
            className="mt-2 self-start !bg-danger hover:!bg-danger-hover !text-white"
          >
            Leave agent
          </Button>
        )}
      </div>
    </Collapsible>
  );
};
